import { useEffect, useMemo, useState } from "react";
import { Alert, CircularProgress, Paper, Stack, Typography } from "@mui/material";
import AccountBalanceWalletOutlined from "@mui/icons-material/AccountBalanceWalletOutlined";
import AttachMoneyRounded from "@mui/icons-material/AttachMoneyRounded";
import BusinessRounded from "@mui/icons-material/BusinessRounded";
import DescriptionOutlined from "@mui/icons-material/DescriptionOutlined";
import Inventory2Outlined from "@mui/icons-material/Inventory2Outlined";
import PercentRounded from "@mui/icons-material/PercentRounded";
import ReceiptLongOutlined from "@mui/icons-material/ReceiptLongOutlined";
import TrendingUpRounded from "@mui/icons-material/TrendingUpRounded";
import AgenteContabilidadeShell from "../components/AgenteContabilidadeShell";
import AliquotasConfigPanel from "../components/AliquotasConfigPanel";
import ChartCard from "../components/ChartCard";
import EmpreendimentosIntersecaoNotice from "../components/EmpreendimentosIntersecaoNotice";
import FilterSelect from "../components/FilterSelect";
import KpiGrid from "../components/KpiGrid";
import MonthFallbackNotice from "../components/MonthFallbackNotice";
import PeriodSelector from "../components/PeriodSelector";
import { useAgenteContabilidadeStore } from "../hooks/useAgenteContabilidadeStore";
import { usePageEmpresaSelection } from "../hooks/usePageEmpresaSelection";
import { usePageMonthSelection } from "../hooks/usePageMonthSelection";
import {
  logContabilidadeApiError,
  obterConfig,
  obterImpostos,
  salvarConfig as salvarConfigApi,
} from "../services/agenteContabilidadeApiService";
import {
  buildEmpresaFilterParams,
  buildEmpreendimentoOptions,
  findEmpreendimentoByFilterValue,
  getEmpreendimentoFilterValue,
  isTodosEmpreendimentos,
  normalizeEmpreendimentoContext,
  TODOS_EMPREENDIMENTOS_VALUE,
} from "../utils/empreendimentoSelector";
import {
  consolidateTimelineByMonth,
  getIntersecaoForMonth,
  normalizeIntersecaoList,
  normalizeIntersecaoPorMes,
  resolveActiveMonth,
} from "../utils/intersecaoEmpreendimentos";
import {
  TAX_REGIMES,
  getDefaultTaxAliquotas,
  getTotalAliquota,
  normalizeTaxRegimeAliquotas,
} from "../services/dreService";
import { colors } from "../constants/designTokens";
import { formatCurrency, formatPercent } from "../utils/formatters";

const getFirstValue = (item, keys, fallback = 0) => {
  for (const key of keys) {
    if (item?.[key] !== undefined && item?.[key] !== null) {
      return item[key];
    }
  }

  return fallback;
};

const toNumber = (value) => {
  const numberValue = Number(value || 0);
  return Number.isFinite(numberValue) ? numberValue : 0;
};

function normalizeImpostosItem(item) {
  const pctImpostosVgv = toNumber(getFirstValue(item, ["pctImpostosVgv", "percentualImpostosVgv", "pct_impostos_vgv"]));
  const taxRate = pctImpostosVgv > 0 ? pctImpostosVgv / 100 : 0;
  const getBaseFromTax = (value) => (taxRate > 0 ? toNumber(value) / taxRate : 0);

  return {
    mes_ano: getFirstValue(item, ["mes_ano", "mesAno", "competencia", "mes"], ""),
    idClienteEmpresa: getFirstValue(item, ["idClienteEmpresa", "id_cliente_empresa"], ""),
    nomeEmpresa: getFirstValue(item, ["nomeEmpresa", "nome_empresa"], ""),
    vgvTotal: toNumber(getFirstValue(item, ["vgvTotal", "vgv_total"])),
    baseReceita: getBaseFromTax(getFirstValue(item, ["impostoReceita", "imposto_receita"])),
    baseRecebimentos: getBaseFromTax(getFirstValue(item, ["impostoRecebimentos", "imposto_recebimentos"])),
    baseContasReceber: getBaseFromTax(getFirstValue(item, ["impostoContasReceber", "imposto_contas_receber"])),
    baseEstoque: getBaseFromTax(getFirstValue(item, ["impostoEstoque", "imposto_estoque"])),
    baseReceitaProduzir: getBaseFromTax(getFirstValue(item, ["impostoReceitaProduzir", "imposto_receita_produzir"])),
    pctImpostosVgv,
  };
}

function applyTaxRateToTimeline(timeline, taxRatePercent) {
  const taxRate = Number(taxRatePercent || 0) / 100;

  return (timeline || []).map((item) => {
    const vgvTotal = toNumber(item.vgvTotal);
    const impostoReceita = toNumber(item.baseReceita) * taxRate;
    const impostoRecebimentos = toNumber(item.baseRecebimentos) * taxRate;
    const impostoVgvTotal = vgvTotal * taxRate;

    return {
      ...item,
      impostoReceita,
      impostoRecebimentos,
      impostoContasReceber: toNumber(item.baseContasReceber) * taxRate,
      impostoEstoque: toNumber(item.baseEstoque) * taxRate,
      impostoReceitaProduzir: toNumber(item.baseReceitaProduzir) * taxRate,
      impostoVgvTotal,
      impostosDevidos: impostoVgvTotal - impostoReceita,
      pctImpostosVgv: vgvTotal > 0 ? (impostoVgvTotal / vgvTotal) * 100 : 0,
    };
  });
}

function normalizeImpostosResponse(payload) {
  if (!payload) {
    return null;
  }

  const sourceTimeline =
    (Array.isArray(payload) && payload) ||
    payload.timeline ||
    payload.grafico ||
    payload.registros ||
    payload.items ||
    payload.data ||
    [];

  if (!Array.isArray(sourceTimeline)) {
    return null;
  }

  const timeline = sourceTimeline.map(normalizeImpostosItem).filter((item) => item.mes_ano);
  const months = Array.isArray(payload.meses)
    ? payload.meses
    : Array.isArray(payload.months)
    ? payload.months
    : timeline.map((item) => item.mes_ano);

  return {
    months,
    timeline,
    empreendimentosIntersecao: normalizeIntersecaoList(
      payload.empreendimentosIntersecao ||
        payload.empreendimentos_intersecao ||
        payload.intersecaoEmpreendimentos ||
        payload.intersecao_empreendimentos
    ),
    empreendimentosIntersecaoPorMes: normalizeIntersecaoPorMes(payload),
  };
}

function normalizeConfig(config, fallbackTaxConfig) {
  if (!config || Array.isArray(config)) {
    return {
      ...fallbackTaxConfig,
      aliquotas: normalizeTaxRegimeAliquotas(
        fallbackTaxConfig.regime,
        fallbackTaxConfig.aliquotas
      ),
    };
  }

  const regime = config.regimeTributario || fallbackTaxConfig.regime;
  const aliquotas = {
    pis: toNumber(getFirstValue(config, ["pis"], fallbackTaxConfig.aliquotas?.pis)),
    cofins: toNumber(getFirstValue(config, ["cofins"], fallbackTaxConfig.aliquotas?.cofins)),
    irpj: toNumber(getFirstValue(config, ["irpj"], fallbackTaxConfig.aliquotas?.irpj)),
    csll: toNumber(getFirstValue(config, ["csll"], fallbackTaxConfig.aliquotas?.csll)),
    iss: toNumber(getFirstValue(config, ["iss"], fallbackTaxConfig.aliquotas?.iss)),
  };

  return {
    regime,
    aliquotas: normalizeTaxRegimeAliquotas(regime, aliquotas),
  };
}

export default function ImpostosPage() {
  const { state, actions } = useAgenteContabilidadeStore();
  const [selectedMonth, setSelectedMonth] = usePageMonthSelection("impostos");
  const [apiAvailableMonths, setApiAvailableMonths] = useState([]);
  const [apiImpostosData, setApiImpostosData] = useState(null);
  const [apiStatus, setApiStatus] = useState("idle");
  const [reloadKey, setReloadKey] = useState(0);
  const [configData, setConfigData] = useState(null);
  const [taxDraft, setTaxDraft] = useState(state.taxConfig);
  const [taxFieldsEdited, setTaxFieldsEdited] = useState(false);
  const [configStatus, setConfigStatus] = useState("idle");
  const [configSaveStatus, setConfigSaveStatus] = useState("idle");
  const [configError, setConfigError] = useState("");

  const totalAliquota = getTotalAliquota(taxDraft.aliquotas);

  const backendEmpreendimentos = useMemo(
    () => state.backendCatalog?.empreendimentos || [],
    [state.backendCatalog?.empreendimentos]
  );
  const backendCatalogSyncKey = state.backendCatalog?.lastSyncAt || "";
  const hasBackendEmpreendimentos = backendEmpreendimentos.length > 0;
  const backendEmpresaOptions = useMemo(
    () => buildEmpreendimentoOptions(backendEmpreendimentos),
    [backendEmpreendimentos]
  );
  const [selectedBackendEmpresa, setSelectedBackendEmpresa] = usePageEmpresaSelection("impostos", backendEmpresaOptions);

  const selectedBackendEmpresaInfo = useMemo(
    () =>
      backendEmpreendimentos.find((item) => {
        const value = getEmpreendimentoFilterValue(item);
        return value === selectedBackendEmpresa;
      }) || null,
    [backendEmpreendimentos, selectedBackendEmpresa]
  );

  useEffect(() => {
    actions.refreshBackendCatalog().catch(() => {});
  }, [actions]);

  useEffect(() => {
    let active = true;

    async function fetchConfig() {
      const fallbackTaxConfig = {
        regime: state.taxConfig.regime,
        aliquotas: normalizeTaxRegimeAliquotas(state.taxConfig.regime, {
          ...getDefaultTaxAliquotas(),
          ...(state.taxConfig.aliquotas || {}),
        }),
      };

      if (!hasBackendEmpreendimentos || !selectedBackendEmpresaInfo) {
        setConfigData(null);
        setTaxDraft(fallbackTaxConfig);
        setTaxFieldsEdited(false);
        setConfigStatus("idle");
        setConfigError("");
        return;
      }

      setConfigStatus("loading");
      setConfigError("");

      try {
        const params = selectedBackendEmpresaInfo.idClienteEmpresa
          ? { idClienteEmpresa: selectedBackendEmpresaInfo.idClienteEmpresa }
          : { codigoEmpresaErp: selectedBackendEmpresaInfo.codigoEmpresaErp || selectedBackendEmpresa };
        const config = await obterConfig(params);

        if (!active) return;

        const nextTaxConfig = normalizeConfig(config, fallbackTaxConfig);
        setConfigData(config);
        setTaxDraft(nextTaxConfig);
        setTaxFieldsEdited(false);
        setConfigStatus("success");
        setConfigSaveStatus("idle");
      } catch (error) {
        if (!active) return;
        logContabilidadeApiError("config-impostos", error);
        setConfigData(null);
        setTaxDraft(fallbackTaxConfig);
        setTaxFieldsEdited(false);
        setConfigStatus("error");
        setConfigError("Não foi possível carregar a configuração tributária da empresa.");
      }
    }

    fetchConfig();

    return () => {
      active = false;
    };
  }, [
    hasBackendEmpreendimentos,
    selectedBackendEmpresa,
    selectedBackendEmpresaInfo,
    state.taxConfig,
  ]);

  useEffect(() => {
    let active = true;

    async function fetchImpostos() {
      setApiStatus("loading");
      setApiImpostosData(null);

      try {
        const params = buildEmpresaFilterParams(selectedBackendEmpresa);
        const payload = await obterImpostos(params);
        const normalizedData = normalizeImpostosResponse(payload);

        if (!active) return;

        if (normalizedData?.timeline?.length) {
          setApiAvailableMonths(normalizedData.months);
          setApiImpostosData(normalizedData);
          setApiStatus("success");
        } else {
          setApiImpostosData(null);
          setApiStatus("empty");
        }
      } catch (error) {
        if (!active) return;
        logContabilidadeApiError("impostos", error);
        setApiStatus("error");
      }
    }

    fetchImpostos();

    return () => {
      active = false;
    };
  }, [backendCatalogSyncKey, reloadKey, selectedBackendEmpresa]);

  const usingApiData = Boolean(apiImpostosData?.timeline?.length);
  const impostosData = useMemo(() => {
    if (usingApiData) {
      return {
        ...apiImpostosData,
        months: apiImpostosData.months,
        timeline: applyTaxRateToTimeline(apiImpostosData.timeline, totalAliquota),
      };
    }

    return { months: [], timeline: [] };
  }, [apiImpostosData, totalAliquota, usingApiData]);
  const { timeline } = impostosData;
  const isTodos = isTodosEmpreendimentos(selectedBackendEmpresa);
  const visibleTimeline = useMemo(
    () => (isTodos ? consolidateTimelineByMonth(timeline, ["pctImpostosVgv"]) : timeline),
    [isTodos, timeline]
  );
  const months = usingApiData && apiAvailableMonths.length
    ? apiAvailableMonths
    : impostosData.months;

  const activeMonth = resolveActiveMonth(months, selectedMonth);

  const currentSnapshot = visibleTimeline.find((item) => item.mes_ano === activeMonth) || visibleTimeline[visibleTimeline.length - 1];
  const intersecaoEmpreendimentos = getIntersecaoForMonth(impostosData, activeMonth);
  const selectedEmpreendimentoContext = isTodos
    ? { filtro: TODOS_EMPREENDIMENTOS_VALUE, nomeEmpresa: "Todos Empreendimentos" }
    : normalizeEmpreendimentoContext(findEmpreendimentoByFilterValue(backendEmpreendimentos, selectedBackendEmpresa));

  const metrics = currentSnapshot
    ? [
        { label: "VGV Total do Empreendimento", value: formatCurrency(currentSnapshot.vgvTotal), variant: "green", icon: ReceiptLongOutlined },
        { label: "Imposto s/ Receita Apropriada", value: formatCurrency(currentSnapshot.impostoReceita), variant: "blue", icon: ReceiptLongOutlined },
        { label: "Imposto s/ Recebimento", value: formatCurrency(currentSnapshot.impostoRecebimentos), variant: "purple", icon: AccountBalanceWalletOutlined },
        { label: "Imposto Diferido s/ Contas a Receber", value: formatCurrency(currentSnapshot.impostoContasReceber * -1), variant: "orange", icon: AccountBalanceWalletOutlined },
        { label: "Imposto s/ VGV em Estoque", value: formatCurrency(currentSnapshot.impostoEstoque), variant: "teal", icon: Inventory2Outlined },
        { label: "Imposto s/ Receita a Produzir", value: formatCurrency(currentSnapshot.impostoReceitaProduzir), variant: "red", icon: TrendingUpRounded },
        { label: "Imposto s/ VGV Total", value: formatCurrency(currentSnapshot.impostoVgvTotal), variant: "navy", icon: AttachMoneyRounded },
        { label: "Imposto Devido a Realizar", value: formatCurrency(currentSnapshot.impostosDevidos), variant: "red", icon: DescriptionOutlined },
        { label: "% de Imposto s/ VGV Total", value: formatPercent(currentSnapshot.pctImpostosVgv, { decimals: 2 }), variant: "magenta", icon: PercentRounded },
      ]
    : [];

  const chartData = visibleTimeline.map((item) => ({
    mes_ano: item.mes_ano,
    impostoReceita: item.impostoReceita / 1000000,
    impostoRecebimentos: item.impostoRecebimentos / 1000000,
    impostoVgvTotal: item.impostoVgvTotal / 1000000,
  }));

  const empreendimentoOptions = backendEmpresaOptions;
  const selectedEmpreendimentoValue = selectedBackendEmpresa;

  function handleEmpreendimentoChange(value) {
    setSelectedMonth((current) => current || activeMonth);
    setSelectedBackendEmpresa(value || "");
    setApiAvailableMonths([]);
    setTaxFieldsEdited(false);
  }

  function handleTaxDraftChange(regime, aliquotas, source) {
    setTaxDraft({
      regime,
      aliquotas: { ...aliquotas },
    });
    setTaxFieldsEdited(source === "aliquota" || source === "regime");
    setConfigSaveStatus("idle");
  }

  async function handleSaveTaxConfig() {
    if (!selectedBackendEmpresaInfo) {
      return;
    }

    setConfigSaveStatus("saving");
    setConfigError("");

    try {
      const payload = {
        ...(selectedBackendEmpresaInfo.idClienteEmpresa
          ? { idClienteEmpresa: selectedBackendEmpresaInfo.idClienteEmpresa }
          : {}),
        codigoEmpresaErp: selectedBackendEmpresaInfo.codigoEmpresaErp || selectedBackendEmpresa,
        regimeTributario: taxDraft.regime,
        pis: toNumber(taxDraft.aliquotas?.pis),
        cofins: toNumber(taxDraft.aliquotas?.cofins),
        irpj: toNumber(taxDraft.aliquotas?.irpj),
        csll: toNumber(taxDraft.aliquotas?.csll),
        iss: toNumber(taxDraft.aliquotas?.iss),
        corretagem: toNumber(getFirstValue(configData, ["corretagem"], state.despesasConfig?.corretagem ?? 5)),
        publicidade: toNumber(getFirstValue(configData, ["publicidade"], state.despesasConfig?.publicidade ?? 2)),
        admCentral: toNumber(getFirstValue(configData, ["admCentral", "adm_central"], state.despesasConfig?.adm_central ?? 5)),
        custoFinanceiro: toNumber(getFirstValue(configData, ["custoFinanceiro", "custo_financeiro"], 3.5)),
        provisaoGarantia: toNumber(getFirstValue(configData, ["provisaoGarantia", "provisao_garantia"], 2)),
      };

      const savedConfig = await salvarConfigApi(payload);
      const nextTaxConfig = normalizeConfig(savedConfig, taxDraft);

      setConfigData(savedConfig);
      setTaxDraft(nextTaxConfig);
      setTaxFieldsEdited(false);
      setConfigSaveStatus("success");
      actions.setState((currentState) => ({
        ...currentState,
        taxConfig: nextTaxConfig,
      }));
      setReloadKey((current) => current + 1);
    } catch (error) {
      logContabilidadeApiError("salvar-config-impostos", error);
      setConfigSaveStatus("error");
    setConfigError("Não foi possível salvar as alíquotas. Tente novamente.");
    }
  }

  const isLoadingImpostos = (apiStatus === "idle" || apiStatus === "loading") && !usingApiData;
  const selectedRegimeAllowsCustomAliquotas = Boolean(TAX_REGIMES[taxDraft.regime]?.editable);
  const hasSavableTaxChanges = taxFieldsEdited;
  const canSaveTaxConfig = hasBackendEmpreendimentos && Boolean(selectedBackendEmpresaInfo);
  const configHelperText = !canSaveTaxConfig
    ? "Selecione uma empresa para carregar e salvar as aliquotas."
    : !selectedRegimeAllowsCustomAliquotas
    ? "Este regime usa aliquotas prontas. Selecione Personalizado para editar e salvar aliquotas."
    : configSaveStatus === "success"
    ? "Alíquotas salvas. Os dados foram recarregados pela API."
    : configStatus === "loading"
    ? "Carregando configuracao tributaria da empresa."
    : configError || "A tela recalcula os impostos ao alterar o regime ou as aliquotas.";

  return (
    <AgenteContabilidadeShell
      title="Gestão de Imposto"
      subtitle="Projeção tributária sobre VGV, receitas e recebimentos"
      updating={apiStatus === "loading" && usingApiData}
      agenteContext={{
        paginaAtual: "impostos",
        empresa: selectedBackendEmpresa,
        competencia: activeMonth,
        dadosVisiveis: currentSnapshot
          ? {
              ...currentSnapshot,
              empresaSelecionada: selectedEmpreendimentoContext,
            }
          : {},
      }}
      actions={[
        <FilterSelect
          key="emp"
          value={selectedEmpreendimentoValue}
          onChange={handleEmpreendimentoChange}
          options={empreendimentoOptions}
          icon={BusinessRounded}
          minWidth={286}
        />,
      ]}
    >
      {isLoadingImpostos ? (
        <Paper elevation={0} sx={{ borderRadius: 3, bgcolor: colors.surface, p: 4, border: `1px solid ${colors.border}` }}>
          <Stack alignItems="center" spacing={1.5} sx={{ textAlign: "center" }}>
            <CircularProgress size={34} />
            <Typography sx={{ fontWeight: 900, color: colors.text }}>
              Carregando dados de impostos
            </Typography>
            <Typography sx={{ color: colors.muted, fontSize: 13 }}>
              Aguarde enquanto consultamos os indicadores calculados pela API.
            </Typography>
          </Stack>
        </Paper>
      ) : !visibleTimeline.length ? (
        <Paper elevation={0} sx={{ borderRadius: 3, bgcolor: colors.surface, p: 4, border: `1px solid ${colors.border}` }}>
          <Stack alignItems="center" spacing={1.2} sx={{ textAlign: "center" }}>
            <Typography sx={{ fontWeight: 900, color: colors.text, fontSize: 20 }}>
              Nenhum dado disponível
            </Typography>
            <Typography sx={{ color: colors.muted, fontSize: 14, maxWidth: 520 }}>
              Não encontramos dados disponíveis para exibição no momento.
            </Typography>
          </Stack>
        </Paper>
      ) : (
        <>
          <PeriodSelector months={months} value={activeMonth} onChange={setSelectedMonth} />
          <MonthFallbackNotice requestedMonth={selectedMonth} activeMonth={activeMonth} />
          <EmpreendimentosIntersecaoNotice
            isTodosEmpreendimentos={isTodos}
            activeMonth={activeMonth}
            empreendimentos={intersecaoEmpreendimentos}
          />
          <AliquotasConfigPanel
            regime={taxDraft.regime}
            aliquotas={taxDraft.aliquotas}
            onChange={handleTaxDraftChange}
            onSave={handleSaveTaxConfig}
            saveDisabled={!canSaveTaxConfig || configStatus === "loading"}
            saving={configSaveStatus === "saving"}
            hasChanges={hasSavableTaxChanges}
            helperText={configHelperText}
          />
          {usingApiData && selectedRegimeAllowsCustomAliquotas && taxFieldsEdited ? (
            <Alert severity="info" variant="outlined" sx={{ mt: 2, mb: 2 }}>
              Os indicadores ja foram recalculados no front com as aliquotas em tela.
              Clique em Salvar aliquotas para persistir a configuracao no backend.
            </Alert>
          ) : null}
          <KpiGrid metrics={metrics} />
          <ChartCard
            title="Evolução dos Impostos"
            data={chartData}
            series={[
              { key: "impostoReceita", name: "Imp. s/ Receita", color: "#6366f1" },
              { key: "impostoRecebimentos", name: "Imp. s/ Recebimentos", color: "#8b5cf6" },
              { key: "impostoVgvTotal", name: "Imp. s/ VGV Total", color: "#64748b" },
            ]}
            headerGradient="purple"
          />
        </>
      )}
    </AgenteContabilidadeShell>
  );
}
